import { Injectable } from '@angular/core';
import { ObservationReferenceRangeTemplate } from './observation-reference-range.service';
import { Coding, CodeableConcept } from 'fhir/r4';

/**
 * Service to handle CSV/XLSX import/export of observation reference range templates.
 * CSV Format:
 *   observationCode | observationDisplay | rangeType | lowValue | lowUnit | highValue | highUnit | appliesTo_gender | ageLowValue | ageLowUnit | ageHighValue | ageHighUnit | text
 *   (where appliesTo_gender is one of: female, male, other, pregnant, or empty for "all")
 */
@Injectable({ providedIn: 'root' })
export class ReferenceRangeImportExportService {
  private readonly unitSystem = 'http://unitsofmeasure.org';

  /**
   * Generate a CSV template string with expected headers and one empty example row.
   */
  generateCsvTemplate(): string {
    const headers = [
      'observationCode',
      'observationDisplay',
      'rangeType',
      'lowValue',
      'lowUnit',
      'highValue',
      'highUnit',
      'appliesToGender',
      'ageLowValue',
      'ageLowUnit',
      'ageHighValue',
      'ageHighUnit',
      'text'
    ];

    // Example row
    const exampleRow = [
      '8867-4',
      'Heart rate',
      'normal',
      '60',
      '/min',
      '100',
      '/min',
      'all',
      '',
      '',
      '',
      '',
      'Normal heart rate range for healthy adults'
    ];

    return [headers.join(','), exampleRow.join(',')].join('\n');
  }

  /**
   * Download CSV template as a file.
   */
  downloadCsvTemplate(): void {
    const csv = this.generateCsvTemplate();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reference-range-template-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  /**
   * Parse CSV file content and convert to templates.
   * Expects CSV with headers matching generateCsvTemplate().
   */
  parseCSV(csv: string): { templates: ObservationReferenceRangeTemplate[]; errors: string[] } {
    const errors: string[] = [];
    const templates: ObservationReferenceRangeTemplate[] = [];

    try {
      const lines = csv.trim().split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        errors.push('CSV must contain at least a header row and one data row.');
        return { templates, errors };
      }

      // Parse header
      const headerLine = lines[0];
      const headers = this.parseCSVLine(headerLine);
      const expectedHeaders = [
        'observationCode',
        'observationDisplay',
        'rangeType',
        'lowValue',
        'lowUnit',
        'highValue',
        'highUnit',
        'appliesToGender',
        'ageLowValue',
        'ageLowUnit',
        'ageHighValue',
        'ageHighUnit',
        'text'
      ];

      // Validate headers
      if (headers.length !== expectedHeaders.length) {
        errors.push(
          `Header mismatch. Expected ${expectedHeaders.length} columns: ${expectedHeaders.join(', ')}`
        );
        return { templates, errors };
      }

      // Parse data rows
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Skip empty lines

        const values = this.parseCSVLine(line);
        if (values.length !== headers.length) {
          errors.push(`Row ${i + 1}: expected ${headers.length} columns, got ${values.length}`);
          continue;
        }

        const template = this.rowToTemplate(values, headers, i + 1);
        if (template) {
          templates.push(template);
        } else {
          errors.push(`Row ${i + 1}: failed to parse (missing required fields)`);
        }
      }

      return { templates, errors };
    } catch (e) {
      errors.push(`CSV parsing error: ${(e as any)?.message || String(e)}`);
      return { templates, errors };
    }
  }

  /**
   * Convert parsed templates to CSV export format.
   */
  templatesToCSV(templates: ObservationReferenceRangeTemplate[]): string {
    const headers = [
      'observationCode',
      'observationDisplay',
      'rangeType',
      'lowValue',
      'lowUnit',
      'highValue',
      'highUnit',
      'appliesToGender',
      'ageLowValue',
      'ageLowUnit',
      'ageHighValue',
      'ageHighUnit',
      'text'
    ];

    const rows = [headers.join(',')];

    for (const template of templates) {
      const code = template.code?.code ?? '';
      const display = template.display ?? '';
      const type = template.range?.type?.coding?.[0]?.code ?? 'normal';
      const lowValue = template.range?.low?.value ?? '';
      const lowUnit = template.range?.low?.unit || template.range?.low?.code || '';
      const highValue = template.range?.high?.value ?? '';
      const highUnit = template.range?.high?.unit || template.range?.high?.code || '';
      const genderCode = this.extractGenderFromAppliesto(template.range?.appliesTo);
      const ageLowValue = template.range?.age?.low?.value ?? '';
      const ageLowUnit = template.range?.age?.low?.code || template.range?.age?.low?.unit || '';
      const ageHighValue = template.range?.age?.high?.value ?? '';
      const ageHighUnit = template.range?.age?.high?.code || template.range?.age?.high?.unit || '';
      const text = template.range?.text ?? '';

      const row = [
        code,
        display,
        type,
        lowValue,
        lowUnit,
        highValue,
        highUnit,
        genderCode,
        ageLowValue,
        ageLowUnit,
        ageHighValue,
        ageHighUnit,
        text
      ];

      rows.push(this.escapeCSVLine(row));
    }

    return rows.join('\n');
  }

  /**
   * Download templates as CSV file.
   */
  downloadTemplatesAsCSV(templates: ObservationReferenceRangeTemplate[]): void {
    const csv = this.templatesToCSV(templates);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reference-ranges-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  // ============ Private Helpers ============

  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  }

  private escapeCSVLine(values: (string | number)[]): string {
    return values
      .map(v => {
        const str = String(v);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      })
      .join(',');
  }

  private rowToTemplate(
    values: string[],
    headers: string[],
    rowNum: number
  ): ObservationReferenceRangeTemplate | null {
    const getField = (header: string) => {
      const idx = headers.indexOf(header);
      return idx >= 0 ? values[idx] : '';
    };

    const code = getField('observationCode').trim();
    const display = getField('observationDisplay').trim();
    if (!code || !display) {
      return null;
    }

    const coding: Coding = {
      system: 'http://loinc.org',
      code,
      display
    };

    const rangeType = getField('rangeType').trim() || 'normal';
    const lowValue = this.parseNumber(getField('lowValue'));
    const lowUnit = getField('lowUnit').trim();
    const highValue = this.parseNumber(getField('highValue'));
    const highUnit = getField('highUnit').trim();
    const genderCode = getField('appliesToGender').trim();
    const ageLowValue = this.parseNumber(getField('ageLowValue'));
    const ageLowUnit = getField('ageLowUnit').trim() || 'a';
    const ageHighValue = this.parseNumber(getField('ageHighValue'));
    const ageHighUnit = getField('ageHighUnit').trim() || 'a';
    const text = getField('text').trim();

    const range: any = {
      type: { coding: [{ code: rangeType, display: this.capitalizeFirst(rangeType) }] }
    };

    if (lowValue != null) {
      range.low = { value: lowValue, unit: lowUnit, code: lowUnit, system: this.unitSystem };
    }
    if (highValue != null) {
      range.high = { value: highValue, unit: highUnit, code: highUnit, system: this.unitSystem };
    }

    // Parse appliesTo from gender code
    if (genderCode && genderCode.toLowerCase() !== 'all') {
      const genderConcept: CodeableConcept = {
        coding: [{ code: genderCode.toLowerCase(), display: this.capitalizeFirst(genderCode) }]
      };
      range.appliesTo = [genderConcept];
    }

    // Parse age range
    if (ageLowValue != null || ageHighValue != null) {
      const ageRange: any = {};
      if (ageLowValue != null) {
        ageRange.low = { value: ageLowValue, unit: ageLowUnit, code: ageLowUnit, system: this.unitSystem };
      }
      if (ageHighValue != null) {
        ageRange.high = { value: ageHighValue, unit: ageHighUnit, code: ageHighUnit, system: this.unitSystem };
      }
      range.age = ageRange;
    }

    if (text) {
      range.text = text;
    }

    return {
      code: coding,
      display,
      range
    };
  }

  private parseNumber(value: string): number | null {
    const num = Number(value);
    return Number.isNaN(num) || value.trim() === '' ? null : num;
  }

  private capitalizeFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private extractGenderFromAppliesto(appliesTo?: CodeableConcept[]): string {
    if (!appliesTo || appliesTo.length === 0) {
      return 'all';
    }
    const first = appliesTo[0];
    const code = first.coding?.[0]?.code || first.text || 'all';
    return code.toLowerCase();
  }
}
